import { updatedDiff } from "deep-object-diff";

/**
 * Fixed, sensible values for the configuration flags that are MEANINGLESS on a
 * virtual `INCOMING_REFERENCE` object_template_attribute. Its value is computed
 * from incoming references, so it can never be unique, take a default, be
 * (non-)nullable in a meaningful way, raise an update notification, or be
 * full-text indexed. The attribute editor hides these controls for the datatype;
 * these defaults are also force-applied to the submitted payload so a stale
 * toggle (e.g. is_nullable=false set BEFORE the datatype was switched to
 * INCOMING_REFERENCE) is never sent to the backend. `is_hidden` is deliberately
 * NOT listed — it is the one sensible flag (it controls where the incoming list
 * renders). (Stefan, 2026-10-12.)
 */
export const INCOMING_REFERENCE_FLAG_DEFAULTS: Record<string, unknown> = {
    is_nullable: true,
    is_unique: false,
    silent_update: false,
    searchable: false,
    default_value: null,
};

/**
 * Coerce the meaningless configuration flags of an INCOMING_REFERENCE attribute
 * to their sensible fixed values on the submitted payload (see
 * {@link INCOMING_REFERENCE_FLAG_DEFAULTS}). A no-op for every other datatype /
 * record type (only object_template_attribute carries `datatype`), so it is safe
 * to call unconditionally on any relationship-record submit payload. Pure +
 * unit-tested.
 */
export const applyIncomingReferenceFlagDefaults = <T extends Record<string, any>>(
    payload: T,
    datatype: unknown
): T => (datatype === "INCOMING_REFERENCE" ? { ...payload, ...INCOMING_REFERENCE_FLAG_DEFAULTS } : payload);

export interface RelationshipSubmitPayloadArgs {
    isNew: boolean;
    initialValues: Record<string, any> | undefined;
    formData: Record<string, any>;
    defaultFormValues?: Record<string, any>;
    // The special "value & schema" field key (constants.valueAndSchema). Kept as a
    // parameter so this helper stays free of the heavy ResourcesSingle import graph
    // and can be unit-tested in isolation.
    valueAndSchemaKey?: string;
}

/**
 * Build the payload submitted when saving a relationship record (an
 * object_template_attribute, role_permission, user_git_public_key, …) from the
 * side-panel RelationshipRecordForm.
 *
 * For an EXISTING record we submit only the diff against the fetched initial
 * values (so an edit touches just what changed). For a NEW record there is no
 * meaningful "initial" to diff against — every entered value is an *added* key,
 * and `updatedDiff` drops added keys — so we must submit the FULL form data.
 *
 * Why this matters (regression guard): sending `updatedDiff(initialValues,
 * formData)` on create silently dropped the required `name`/`datatype` (and every
 * other user-entered field), so the create mutation received them as null and the
 * backend rejected it ("Variable '$name' has invalid value: … not to be None").
 * The canvas AttributeCard create path already submits the full formData; this
 * keeps the side-panel path consistent with it. See commit e7ad80330 ("include
 * all form values for a new form"), whose intent this restores.
 */
export const computeRelationshipSubmitPayload = ({
    isNew,
    initialValues,
    formData,
    defaultFormValues = {},
    valueAndSchemaKey = "valueAndSchema",
}: RelationshipSubmitPayloadArgs): Record<string, any> => {
    const changedValues: Record<string, any> = isNew
        ? { ...formData }
        : (updatedDiff(initialValues ?? {}, formData) as Record<string, any>);
    if (formData[valueAndSchemaKey]) {
        changedValues[valueAndSchemaKey] = formData[valueAndSchemaKey];
    }
    // Coerce the meaningless flags to sensible values when the (effective)
    // datatype is INCOMING_REFERENCE — keyed off formData so it covers both a
    // create (full form data) and a datatype switch (the hidden toggles may carry
    // stale user values). See applyIncomingReferenceFlagDefaults.
    return applyIncomingReferenceFlagDefaults(
        {
            ...(isNew ? defaultFormValues : {}),
            ...changedValues,
            id: formData.id,
        },
        formData.datatype
    );
};
